import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, X, User, Users, Building2, Briefcase, Home, Loader2 } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { condoApi, makeAbsolutePhotoUrl, fetchWithAuth } from '../../../services/api';
import { logger } from '../../../services/logger';
import { searchIncludes } from '../../../utils/textNormalization';
import { normalizeCPF, isCPFFormat, formatCPF } from '../../../utils/cpfUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { Accordion } from '../../ui/Accordion';
import toast from 'react-hot-toast';
import { ReentryConfirmationModal } from './ReentryConfirmationModal';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';

// Custom scrollbar styles matching Botoeira and EventsPanel design
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #111827;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #374151;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #4b5563;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #374151 #111827;
  }

  /* Pulsing border animation for visitor search */
  @keyframes pulse-border {
    0%, 100% {
      border-color: #3b82f6;
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
    }
    50% {
      border-color: #60a5fa;
      box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    }
  }

  .pulse-border {
    animation: pulse-border 2s ease-in-out infinite;
  }
`;

interface QuickSearchBarProps {
  customerId: string | null;
  customerType: 'condominio' | 'empresa';
  onPersonClick: (personId: string) => void;
  onUnitClick?: (unitId: string, unitName?: string) => void;
  onRegisterVisitor?: (
    prefilledData?: { cpf?: string; firstName?: string; lastName?: string },
    personId?: string,  // Just pass the ID, modal will fetch everything
    personData?: any  // Full person object with photos (to avoid re-fetching)
  ) => void;
  onRegisterResident?: () => void;
  onRegisterEmployee?: () => void;
  residentsRefreshTrigger?: number;
  onLoadingChange?: (isLoading: boolean) => void;
}

interface SearchResult {
  id: string;
  name: string;
  subtitle?: string;
  photo?: string | null;
  type: 'visitor' | 'resident' | 'employee' | 'unit' | 'department';
}

const QuickSearchBarComponent: React.FC<QuickSearchBarProps> = ({
  customerId,
  customerType,
  onPersonClick,
  onUnitClick,
  onRegisterVisitor,
  onRegisterResident,
  onRegisterEmployee,
  residentsRefreshTrigger,
  onLoadingChange,
}) => {
  // Search terms for each field - restore from localStorage
  const [visitorSearch, setVisitorSearch] = useState(() => {
    try {
      return localStorage.getItem('panther_visitor_search') || '';
    } catch (e) {
      return '';
    }
  });
  const [peopleSearch, setPeopleSearch] = useState(() => {
    try {
      return localStorage.getItem('panther_people_search') || '';
    } catch (e) {
      return '';
    }
  });
  const [locationSearch, setLocationSearch] = useState(() => {
    try {
      return localStorage.getItem('panther_location_search') || '';
    } catch (e) {
      return '';
    }
  });

  // Debounced search terms (300ms delay for server-side search)
  const debouncedVisitorSearch = useDebounce(visitorSearch, 300);
  const debouncedPeopleSearch = useDebounce(peopleSearch, 300);
  const debouncedLocationSearch = useDebounce(locationSearch, 300);

  // Search results from server
  const [visitorResults, setVisitorResults] = useState<SearchResult[]>([]);
  const [peopleResults, setPeopleResults] = useState<SearchResult[]>([]);
  const [locationResults, setLocationResults] = useState<SearchResult[]>([]);

  // Loading states for each search box
  const [isLoadingVisitors, setIsLoadingVisitors] = useState(false);
  const [isLoadingPeople, setIsLoadingPeople] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Notify parent whenever any loading state changes
  useEffect(() => {
    const isLoading = isLoadingVisitors || isLoadingPeople || isLoadingLocations;
    onLoadingChange?.(isLoading);
  }, [isLoadingVisitors, isLoadingPeople, isLoadingLocations, onLoadingChange]);

  // Save search terms to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('panther_visitor_search', visitorSearch);
    } catch (e) {
      logger.warn('QuickSearchBar', 'Failed to save visitor search to localStorage');
    }
  }, [visitorSearch]);

  useEffect(() => {
    try {
      localStorage.setItem('panther_people_search', peopleSearch);
    } catch (e) {
      logger.warn('QuickSearchBar', 'Failed to save people search to localStorage');
    }
  }, [peopleSearch]);

  useEffect(() => {
    try {
      localStorage.setItem('panther_location_search', locationSearch);
    } catch (e) {
      logger.warn('QuickSearchBar', 'Failed to save location search to localStorage');
    }
  }, [locationSearch]);

  // Active dropdown and selected index for keyboard navigation
  const [activeDropdown, setActiveDropdown] = useState<'visitor' | 'people' | 'location' | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Mobile accordion state
  const isMobile = useIsMobile();
  const [expandedAccordion, setExpandedAccordion] = useState<'visitor' | 'people' | 'location' | null>(null);

  // Re-entry confirmation state
  const [showReentryConfirmation, setShowReentryConfirmation] = useState(false);
  const [reentryVisitorInfo, setReentryVisitorInfo] = useState<{
    personId: string;
    roleId: string;
    personName: string;
    unit: string;
    entryTime: string;
    personData: any;
  } | null>(null);

  // Refs for dropdowns and inputs
  const visitorRef = useRef<HTMLDivElement>(null);
  const peopleRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const visitorInputRef = useRef<HTMLInputElement>(null);
  const peopleInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const resultItemsRef = useRef<(HTMLButtonElement | null)[]>([]);

  // Timeout refs for delayed dropdown close
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Server-side search for visitors when debounced search term changes
  useEffect(() => {
    if (!customerId || !debouncedVisitorSearch.trim()) {
      setVisitorResults([]);
      return;
    }

    const searchVisitors = async () => {
      setIsLoadingVisitors(true);
      try {
        const results = await searchVisitorsAPI(customerId, debouncedVisitorSearch);
        setVisitorResults(results.slice(0, 10)); // Limit to 10 results for dropdown
      } catch (error) {
        logger.error('QuickSearchBar', 'Error searching visitors', error);
        setVisitorResults([]);
      } finally {
        setIsLoadingVisitors(false);
      }
    };

    searchVisitors();
  }, [customerId, debouncedVisitorSearch]);

  // Server-side search for people/residents when debounced search term changes
  useEffect(() => {
    if (!customerId || !debouncedPeopleSearch.trim()) {
      setPeopleResults([]);
      return;
    }

    const searchPeople = async () => {
      setIsLoadingPeople(true);
      try {
        const results = await searchPeopleAPI(customerId, customerType, debouncedPeopleSearch);
        setPeopleResults(results.slice(0, 10)); // Limit to 10 results for dropdown
      } catch (error) {
        logger.error('QuickSearchBar', 'Error searching people', error);
        setPeopleResults([]);
      } finally {
        setIsLoadingPeople(false);
      }
    };

    searchPeople();
  }, [customerId, customerType, debouncedPeopleSearch, residentsRefreshTrigger]);

  // Server-side search for locations (units/departments)
  useEffect(() => {
    if (!customerId || !debouncedLocationSearch.trim()) {
      setLocationResults([]);
      return;
    }

    const searchLocations = async () => {
      setIsLoadingLocations(true);
      try {
        if (customerType === 'empresa') {
          // Departments - keep existing (smaller dataset)
          const response = await condoApi.getDepartments(customerId);
          const data = response.data?.data || response.data || [];

          const filtered = data.filter((dept: any) =>
            searchIncludes(dept.name, debouncedLocationSearch) ||
            (dept.manager_name && searchIncludes(dept.manager_name, debouncedLocationSearch))
          ).map((dept: any) => ({
            id: dept.id,
            name: dept.name,
            subtitle: dept.manager_name || '',
            photo: null,
            type: 'department' as const,
          }));

          setLocationResults(filtered.slice(0, 10));
        } else {
          // Units - NEW: server-side search
          const response = await condoApi.searchUnits(
            customerId,
            debouncedLocationSearch,
            { limit: 50 }
          );
          const units = response.data?.data || [];

          const mapped = units.map((unit: any) => {
            const parts: string[] = [];
            if (unit.block_name) parts.push(unit.block_name);
            if (unit.street_name) parts.push(unit.street_number ? `${unit.street_name}, ${unit.street_number}` : unit.street_name);
            parts.push(unit.number);
            return {
              id: unit.id,
              name: parts.join(' – '),
              subtitle: unit.type || '',
              photo: null,
              type: 'unit' as const,
            };
          });

          setLocationResults(mapped.slice(0, 10));
        }
      } catch (error) {
        logger.error('QuickSearchBar', 'Error searching locations', error);
        setLocationResults([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    searchLocations();
  }, [customerId, customerType, debouncedLocationSearch]);

  // Server-side search for visitors with search parameter
  const searchVisitorsAPI = async (custId: string, searchTerm: string): Promise<SearchResult[]> => {
    try {
      // Normalize CPF if the search term is a CPF format (remove dots and dashes)
      const normalizedSearch = isCPFFormat(searchTerm) ? normalizeCPF(searchTerm) : searchTerm;
      // Search ALL people (no role_type) so that Pré-Cadastro people (no roles) are included
      const response = await condoApi.getPeople(custId, {
        search: normalizedSearch,
        page_size: 50,
      });
      const data = response.data?.data || response.data || [];

      const results: SearchResult[] = [];
      for (const person of data) {
        const visitorRole = (person.roles || []).find((r: any) =>
          r.role_type === 'visitor' || r.role_type === 'service_provider'
        );
        const hasAnyRole = (person.roles || []).length > 0;
        const hasNonVisitorRoleOnly = hasAnyRole && !visitorRole;

        // Skip people who only have resident/staff roles — they appear in the residents section
        if (hasNonVisitorRoleOnly) continue;

        results.push({
          id: person.id,
          name: `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Visitante',
          subtitle: !hasAnyRole
            ? 'Pré-Cadastro'
            : (visitorRole?.role_metadata?.authorized_by_name || visitorRole?.role_metadata?.host_unit_name || ''),
          photo: makeAbsolutePhotoUrl(
            person.profile_picture_url ||
            person.identity_photo_url ||
            person.identity_photo?.photo_url
          ),
          type: 'visitor' as const,
        });
      }
      return results;
    } catch (error) {
      logger.error('QuickSearchBar', 'Error searching visitors', error);
      return [];
    }
  };

  // Server-side search for people (residents/employees) with search parameter
  const searchPeopleAPI = async (custId: string, custType: string, searchTerm: string): Promise<SearchResult[]> => {
    try {
      // Normalize CPF if the search term is a CPF format (remove dots and dashes)
      const normalizedSearch = isCPFFormat(searchTerm) ? normalizeCPF(searchTerm) : searchTerm;
      let data: any[] = [];

      if (custType === 'empresa') {
        const response = await fetchWithAuth(`/api/v1/customers/${custId}/employees?search=${encodeURIComponent(normalizedSearch)}&limit=50`);
        if (response.ok) {
          const json = await response.json();
          data = json.data || json.employees || json || [];
        }
      } else {
        const response = await condoApi.getPeople(custId, {
          role_type: 'resident',
          search: normalizedSearch,  // Backend will search across ALL residents
          limit: 50  // Get more results from server, then limit to 10 for dropdown
        });
        data = response.data?.data || response.data || [];
      }

      return data.map((person: any) => {
        let unitOrDept = person.department_name || '';
        if (!unitOrDept && person.roles) {
          const role = person.roles?.find((r: any) => r.role_type === 'resident' || r.role_type === 'employee');
          unitOrDept = role?.role_metadata?.unit_name || role?.role_metadata?.department_name || '';
        }

        return {
          id: person.id,
          name: `${person.first_name || ''} ${person.last_name || ''}`.trim(),
          subtitle: unitOrDept,
          photo: makeAbsolutePhotoUrl(
            person.profile_picture_url ||
            person.identity_photo_url ||
            person.identity_photo?.photo_url
          ),
          type: custType === 'empresa' ? 'employee' as const : 'resident' as const,
        };
      });
    } catch (error) {
      logger.error('QuickSearchBar', 'Error searching people', error);
      return [];
    }
  };

  // Results now come from server-side search (useEffect hooks above)
  // No need for client-side filtering

  // Get current results based on active dropdown
  const getCurrentResults = useCallback(() => {
    switch (activeDropdown) {
      case 'visitor': return visitorResults;
      case 'people': return peopleResults;
      case 'location': return locationResults;
      default: return [];
    }
  }, [activeDropdown, visitorResults, peopleResults, locationResults]);

  // Auto-select first result when results change (for keyboard navigation)
  useEffect(() => {
    // Auto-select first result if there are any results
    if (activeDropdown === 'visitor' && visitorResults.length > 0) {
      setSelectedIndex(0);
    } else if (activeDropdown === 'people' && peopleResults.length > 0) {
      setSelectedIndex(0);
    } else if (activeDropdown === 'location' && locationResults.length > 0) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(-1);
    }
  }, [visitorResults, peopleResults, locationResults, activeDropdown]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        visitorRef.current && !visitorRef.current.contains(event.target as Node) &&
        peopleRef.current && !peopleRef.current.contains(event.target as Node) &&
        locationRef.current && !locationRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Global keyboard listener - focus visitor search when user starts typing OR presses F2/F3/F4
  useEffect(() => {
    const handleGlobalKeyPress = (event: KeyboardEvent) => {
      // F2 shortcut - focus visitor search
      if (event.key === 'F2') {
        event.preventDefault();
        if (visitorInputRef.current) {
          visitorInputRef.current.focus();
          setActiveDropdown('visitor');
        }
        return;
      }

      // F3 shortcut - focus people search (moradores/funcionários)
      if (event.key === 'F3') {
        event.preventDefault();
        if (peopleInputRef.current) {
          peopleInputRef.current.focus();
          setActiveDropdown('people');
        }
        return;
      }

      // F4 shortcut - focus location search (unidades/departamentos)
      if (event.key === 'F4') {
        event.preventDefault();
        if (locationInputRef.current) {
          locationInputRef.current.focus();
          setActiveDropdown('location');
        }
        return;
      }

      // Only handle if:
      // 1. No input/textarea/contentEditable is focused
      // 2. Not a modifier key
      // 3. Is a printable character
      const activeElement = document.activeElement;
      const isInputFocused = activeElement instanceof HTMLInputElement ||
                            activeElement instanceof HTMLTextAreaElement ||
                            (activeElement instanceof HTMLElement && activeElement.isContentEditable);

      if (!isInputFocused && !event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1) {
        // Focus visitor search and set the typed character
        if (visitorInputRef.current) {
          visitorInputRef.current.focus();
          setActiveDropdown('visitor');
          // The character will be handled by the input naturally
        }
      }
    };

    document.addEventListener('keypress', handleGlobalKeyPress);
    document.addEventListener('keydown', handleGlobalKeyPress); // F2 is a keydown event
    return () => {
      document.removeEventListener('keypress', handleGlobalKeyPress);
      document.removeEventListener('keydown', handleGlobalKeyPress);
    };
  }, []);

  const handleResultClick = useCallback(async (result: SearchResult) => {
    // Clear search terms but DON'T set activeDropdown to null - this allows
    // the user to immediately start typing again without clicking outside first
    setSelectedIndex(-1);
    setVisitorSearch('');
    setPeopleSearch('');
    setLocationSearch('');

    // Use setTimeout to ensure state is cleared before callback triggers parent re-render
    setTimeout(async () => {
      if (result.type === 'unit' || result.type === 'department') {
        logger.info('QuickSearchBar', 'Location selected', { type: result.type, id: result.id, name: result.name });
        if (onUnitClick) {
          onUnitClick(result.id, result.name);
        }
      } else if (result.type === 'visitor' && customerId && onRegisterVisitor) {
        // For visitors, check if they're currently inside
        try {
          // Use /people/{id}/full endpoint (same as PersonDetailModal) to get ALL data including photos
          const response = await fetchWithAuth(`/api/v1/people/${result.id}/full`);
          if (response.ok) {
            const data = await response.json();
            const person = data.data || data;

            console.log(' QuickSearchBar: Person data received:', {
              id: person.id,
              name: `${person.first_name} ${person.last_name}`,
              identity_photo_url: person.identity_photo_url,
              profile_picture_url: person.profile_picture_url,
              document_front_url: person.document_front_url,
              document_back_url: person.document_back_url,
              vehicles: person.vehicles,
              hasRoles: !!person.roles,
              roles: person.roles?.map((r: any) => ({
                role_type: r.role_type,
                visitor_state: r.role_metadata?.visitor_state,
                last_entry: r.role_metadata?.last_entry_at,
                last_exit: r.role_metadata?.last_exit_at
              }))
            });

            // Check if visitor is currently inside
            const activeVisit = person.roles?.find((role: any) =>
              (role.role_type === 'visitor' || role.role_type === 'service_provider') &&
              role.role_metadata?.visitor_state === 'inside'
            );

            console.log(' Active visit check:', {
              found: !!activeVisit,
              activeVisit: activeVisit ? {
                id: activeVisit.id,
                role_type: activeVisit.role_type,
                visitor_state: activeVisit.role_metadata?.visitor_state
              } : null
            });

            if (activeVisit) {
              // Visitor is inside - show re-entry confirmation modal
              console.log(' Visitor is currently inside, showing re-entry confirmation');

              // Extract unit information from role metadata
              const unitDisplay = activeVisit.role_metadata?.unit_display ||
                                 activeVisit.role_metadata?.unit ||
                                 'Não especificada';
              const entryTime = activeVisit.role_metadata?.last_entry_at ||
                               activeVisit.created_at;

              const reentryDocPhotos: any[] = person.identity_photos || [];
              const reentryPersonData = {
                ...person,
                document_front_url: reentryDocPhotos.find((p: any) => p.photo_type === 'document_front')?.photo_url || person.document_front_url,
                document_back_url: reentryDocPhotos.find((p: any) => p.photo_type === 'document_back')?.photo_url || person.document_back_url,
              };
              setReentryVisitorInfo({
                personId: person.id,
                roleId: activeVisit.id,
                personName: `${person.first_name} ${person.last_name}`,
                unit: unitDisplay,
                entryTime: entryTime,
                personData: reentryPersonData
              });
              setShowReentryConfirmation(true);
              setActiveDropdown(null); // Close dropdown
              setVisitorSearch(''); // Clear search
            } else {
              // Visitor is NOT inside - open new visit modal with full person data (including photos)
              // Extract presigned document URLs from identity_photos array (same as PersonDetailModal)
              const docPhotos: any[] = person.identity_photos || [];
              const documentFrontUrl = docPhotos.find((p: any) => p.photo_type === 'document_front')?.photo_url || person.document_front_url;
              const documentBackUrl = docPhotos.find((p: any) => p.photo_type === 'document_back')?.photo_url || person.document_back_url;
              onRegisterVisitor(undefined, person.id, {
                ...person,
                document_front_url: documentFrontUrl,
                document_back_url: documentBackUrl,
              });
            }
          }
        } catch (error) {
          logger.error('QuickSearchBar', 'Error checking visitor status', error as Error);
          // On error, just open person modal
          onPersonClick(result.id);
        }
      } else {
        onPersonClick(result.id);
      }
    }, 0);
  }, [onPersonClick, onUnitClick, onRegisterVisitor, customerId]);

  const clearSearch = (field: 'visitor' | 'people' | 'location') => {
    if (field === 'visitor') {
      setVisitorSearch('');
    } else if (field === 'people') {
      setPeopleSearch('');
    } else {
      setLocationSearch('');
    }
    setSelectedIndex(-1);
  };

  // Handle re-entry confirmation
  const handleReentryConfirm = async () => {
    if (!reentryVisitorInfo || !customerId) return;

    console.log(' Re-entry confirmed, checking out current visit and opening registration');

    try {
      // Check out the current visit
      await condoApi.manualCheckOut(
        customerId,
        reentryVisitorInfo.personId,
        reentryVisitorInfo.roleId
      );
      console.log(' Current visit checked out successfully');
      toast.success('Visita anterior finalizada');

      // Open registration modal with visitor data
      if (onRegisterVisitor) {
        onRegisterVisitor(undefined, reentryVisitorInfo.personId, reentryVisitorInfo.personData);
      }

      // Clear state
      setShowReentryConfirmation(false);
      setReentryVisitorInfo(null);
    } catch (error) {
      console.error(' Failed to check out visit:', error);
      toast.error('Erro ao finalizar visita anterior');
    }
  };

  // Handle re-entry cancel
  const handleReentryCancel = () => {
    console.log(' Re-entry cancelled');
    setShowReentryConfirmation(false);
    setReentryVisitorInfo(null);
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback((
    e: React.KeyboardEvent<HTMLInputElement>,
    field: 'visitor' | 'people' | 'location'
  ) => {
    const results = getCurrentResults();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;

      case 'Tab':
        // TAB navigation: visitor → people → location (Shift+TAB for reverse)
        e.preventDefault();

        if (e.shiftKey) {
          // Shift+Tab: go to previous field
          if (field === 'location') {
            peopleInputRef.current?.focus();
          } else if (field === 'people') {
            visitorInputRef.current?.focus();
          }
          // If visitor field, don't go anywhere (it's the first)
        } else {
          // Tab: go to next field
          if (field === 'visitor') {
            peopleInputRef.current?.focus();
          } else if (field === 'people') {
            locationInputRef.current?.focus();
          }
          // If location field, don't go anywhere (it's the last)
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (results.length > 0) {
          // Select highlighted item or first item
          const indexToSelect = selectedIndex >= 0 ? selectedIndex : 0;
          handleResultClick(results[indexToSelect]);
        } else if (field === 'visitor' && visitorSearch.trim() && onRegisterVisitor) {
          // No results found - open visitor modal with prefilled data
          const searchTerm = visitorSearch.trim();
          const prefilledData: { cpf?: string; firstName?: string; lastName?: string } = {};

          if (isCPFFormat(searchTerm)) {
            prefilledData.cpf = normalizeCPF(searchTerm);
          } else if (searchTerm) {
            // Split by space to get first and last name
            const nameParts = searchTerm.split(' ').filter(part => part.length > 0);
            if (nameParts.length > 0) {
              prefilledData.firstName = nameParts[0];
              if (nameParts.length > 1) {
                prefilledData.lastName = nameParts.slice(1).join(' ');
              }
            }
          }

          onRegisterVisitor(prefilledData);
          setActiveDropdown(null);
          setVisitorSearch('');
        }
        break;

      case 'Escape':
        // Clear current search field based on which is active
        if (field === 'visitor') setVisitorSearch('');
        else if (field === 'people') setPeopleSearch('');
        else if (field === 'location') setLocationSearch('');
        setSelectedIndex(-1);
        break;
    }
  }, [activeDropdown, getCurrentResults, selectedIndex, handleResultClick, visitorSearch, onRegisterVisitor]);

  const renderResults = (
    results: SearchResult[],
    searchTerm: string,
    field: 'visitor' | 'people' | 'location'
  ) => {
    // Check if this specific field is loading
    const isLoading = field === 'visitor' ? isLoadingVisitors :
                     field === 'people' ? isLoadingPeople :
                     isLoadingLocations;

    if (isLoading) {
      return (
        <div className="p-4 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
          <span className="text-sm text-gray-400">Buscando...</span>
        </div>
      );
    }

    if (searchTerm.trim() && results.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500 text-sm">
          Nenhum resultado encontrado
        </div>
      );
    }

    return results.map((result, index) => (
      <button
        key={result.id}
        ref={el => resultItemsRef.current[index] = el}
        onClick={() => handleResultClick(result)}
        onMouseEnter={() => setSelectedIndex(index)}
        data-allow-tab="true"
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 transition-colors text-left",
          selectedIndex === index ? "bg-blue-600/30" : "hover:bg-gray-800"
        )}
      >
        {field !== 'location' && (result.photo ? (
          <img
            src={getAbsoluteImageUrl(result.photo)}
            alt={result.name}
            className="w-[26px] h-8 rounded-md object-cover flex-shrink-0"
          />
        ) : (
          <div className={cn(
            "h-8 w-[26px] rounded-md flex items-center justify-center flex-shrink-0",
            result.type === 'visitor' && "bg-gradient-to-br from-teal-500 to-teal-600",
            result.type === 'resident' && "bg-gradient-to-br from-blue-500 to-blue-600",
            result.type === 'employee' && "bg-gradient-to-br from-green-500 to-green-600",
          )}>
            {result.type === 'visitor' && <User className="h-4 w-4 text-white" />}
            {result.type === 'resident' && <Users className="h-4 w-4 text-white" />}
            {result.type === 'employee' && <Users className="h-4 w-4 text-white" />}
          </div>
        ))}
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium text-gray-200", field === 'location' ? "line-clamp-2" : "truncate")}>{result.name}</p>
          {result.subtitle && (
            <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
          )}
        </div>
        {selectedIndex === index && (
          <kbd className="inline-flex items-center px-2 py-1 text-xs font-bold bg-green-600 text-white rounded border border-green-500 shadow-lg flex-shrink-0 animate-pulse">
            ENTER
          </kbd>
        )}
      </button>
    ));
  };

  if (!customerId) return null;

  // Labels based on customer type
  const peopleLabel = customerType === 'empresa' ? 'Funcionários' : 'Moradores';
  const locationLabel = customerType === 'empresa' ? 'Departamentos' : 'Unidades';
  const PeopleIcon = Users;
  const LocationIcon = customerType === 'empresa' ? Briefcase : Building2;

  // Mobile: Render as accordions
  if (isMobile) {
    return (
      <>
        <style>{scrollbarStyles}</style>
        <div className="space-y-2">
          {/* Visitors Accordion */}
          <Accordion
            title="Buscar Visitantes"
            icon={<User className="h-4 w-4" />}
            open={expandedAccordion === 'visitor'}
            onToggle={(isOpen) => setExpandedAccordion(isOpen ? 'visitor' : null)}
          >
            <div className="p-3">
              <div ref={visitorRef} className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    ref={visitorInputRef}
                    type="text"
                    placeholder="Buscar visitante ou prestador de serviço"
                    value={visitorSearch}
                    onChange={(e) => setVisitorSearch(e.target.value)}
                    onFocus={() => {
                      setActiveDropdown('visitor');
                      setSelectedIndex(-1);
                    }}
                    onKeyDown={(e) => handleKeyDown(e, 'visitor')}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-allow-tab="true"
                    className={cn(
                      "w-full pl-9 py-2.5 bg-gray-900 text-gray-200 text-sm rounded-lg border focus:outline-none focus:border-blue-500 placeholder-gray-500",
                      visitorSearch ? "pr-8" : "pr-12",
                      activeDropdown === null && expandedAccordion === null ? "pulse-border" : "border-gray-800"
                    )}
                  />
                  {visitorSearch ? (
                    <button
                      onClick={() => clearSearch('visitor')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-800 rounded"
                    >
                      <X className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  ) : (
                    <kbd className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-gray-800 text-gray-400 rounded border border-gray-700">
                      F2
                    </kbd>
                  )}
                </div>
                {activeDropdown === 'visitor' && (
                  <div className="mt-2 bg-gray-950 rounded-lg border border-gray-800 overflow-hidden max-h-64 overflow-y-auto custom-scrollbar">
                    {onRegisterVisitor && (
                      <button
                        onClick={() => {
                          // Parse search term to extract CPF or name
                          const searchTerm = visitorSearch.trim();
                          const prefilledData: { cpf?: string; firstName?: string; lastName?: string } = {};

                          if (isCPFFormat(searchTerm)) {
                            prefilledData.cpf = normalizeCPF(searchTerm);
                          } else if (searchTerm) {
                            // Split by space to get first and last name
                            const nameParts = searchTerm.split(' ').filter(part => part.length > 0);
                            if (nameParts.length > 0) {
                              prefilledData.firstName = nameParts[0];
                              if (nameParts.length > 1) {
                                prefilledData.lastName = nameParts.slice(1).join(' ');
                              }
                            }
                          }

                          onRegisterVisitor(prefilledData);
                          setActiveDropdown(null);
                          setVisitorSearch('');
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 bg-green-600 hover:bg-green-700 transition-colors text-left border-b border-gray-800"
                      >
                        <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white/20">
                          <User className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white">Cadastrar novo visitante</p>
                          <p className="text-xs text-green-100">
                            {visitorSearch.trim()
                              ? isCPFFormat(visitorSearch)
                                ? `CPF: ${formatCPF(visitorSearch)}`
                                : `Nome: ${visitorSearch}`
                              : 'Clique para adicionar um novo visitante'
                            }
                          </p>
                        </div>
                      </button>
                    )}
                    {visitorSearch.trim() && renderResults(visitorResults, visitorSearch, 'visitor')}
                  </div>
                )}
              </div>
            </div>
          </Accordion>

          {/* People Accordion */}
          <Accordion
            title={`Buscar ${peopleLabel}`}
            icon={<PeopleIcon className="h-4 w-4" />}
            open={expandedAccordion === 'people'}
            onToggle={(isOpen) => setExpandedAccordion(isOpen ? 'people' : null)}
          >
            <div className="p-3">
              <div ref={peopleRef} className="relative">
                <div className="relative">
                  <PeopleIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    ref={peopleInputRef}
                    type="text"
                    placeholder={`Buscar ${peopleLabel.toLowerCase()}`}
                    value={peopleSearch}
                    onChange={(e) => setPeopleSearch(e.target.value)}
                    onFocus={() => {
                      setActiveDropdown('people');
                      setSelectedIndex(-1);
                    }}
                    onKeyDown={(e) => handleKeyDown(e, 'people')}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-allow-tab="true"
                    className={cn(
                      "w-full pl-9 py-2.5 bg-gray-900 text-gray-200 text-sm rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 placeholder-gray-500",
                      peopleSearch ? "pr-8" : "pr-12"
                    )}
                  />
                  {peopleSearch ? (
                    <button
                      onClick={() => clearSearch('people')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-800 rounded"
                    >
                      <X className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  ) : (
                    <kbd className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-gray-800 text-gray-400 rounded border border-gray-700">
                      F3
                    </kbd>
                  )}
                </div>
                {activeDropdown === 'people' && (
                  <div className="mt-2 bg-gray-950 rounded-lg border border-gray-800 overflow-hidden max-h-64 overflow-y-auto custom-scrollbar">
                    {customerType === 'empresa' ? onRegisterEmployee && (
                      <button
                        onClick={() => {
                          onRegisterEmployee();
                          setActiveDropdown(null);
                          setPeopleSearch('');
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 bg-green-600 hover:bg-green-700 transition-colors text-left border-b border-gray-800"
                      >
                        <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white/20">
                          <Users className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white">Cadastrar novo funcionário</p>
                          <p className="text-xs text-green-100">Clique para adicionar um novo funcionário</p>
                        </div>
                      </button>
                    ) : onRegisterResident && (
                      <button
                        onClick={() => {
                          onRegisterResident();
                          setActiveDropdown(null);
                          setPeopleSearch('');
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 bg-green-600 hover:bg-green-700 transition-colors text-left border-b border-gray-800"
                      >
                        <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white/20">
                          <Users className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white">Cadastrar novo morador</p>
                          <p className="text-xs text-green-100">Clique para adicionar um novo morador</p>
                        </div>
                      </button>
                    )}
                    {peopleSearch.trim() && renderResults(peopleResults, peopleSearch, 'people')}
                  </div>
                )}
              </div>
            </div>
          </Accordion>

          {/* Location Accordion */}
          <Accordion
            title={`Buscar ${locationLabel}`}
            icon={<LocationIcon className="h-4 w-4" />}
            open={expandedAccordion === 'location'}
            onToggle={(isOpen) => setExpandedAccordion(isOpen ? 'location' : null)}
          >
            <div className="p-3">
              <div ref={locationRef} className="relative">
                <div className="relative">
                  <LocationIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    ref={locationInputRef}
                    type="text"
                    placeholder={`Buscar ${locationLabel.toLowerCase()}`}
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    onFocus={() => {
                      setActiveDropdown('location');
                      setSelectedIndex(-1);
                    }}
                    onKeyDown={(e) => handleKeyDown(e, 'location')}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-allow-tab="true"
                    className={cn(
                      "w-full pl-9 py-2.5 bg-gray-900 text-gray-200 text-sm rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 placeholder-gray-500",
                      locationSearch ? "pr-8" : "pr-12"
                    )}
                  />
                  {locationSearch ? (
                    <button
                      onClick={() => clearSearch('location')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-800 rounded"
                    >
                      <X className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  ) : (
                    <kbd className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-gray-800 text-gray-400 rounded border border-gray-700">
                      F4
                    </kbd>
                  )}
                </div>
                {activeDropdown === 'location' && (
                  <div className="mt-2 bg-gray-950 rounded-lg border border-gray-800 overflow-hidden max-h-64 overflow-y-auto custom-scrollbar">
                    {!locationSearch.trim() ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        Digite para buscar {customerType === 'empresa' ? 'departamentos' : 'unidades'}
                      </div>
                    ) : (
                      renderResults(locationResults, locationSearch, 'location')
                    )}
                  </div>
                )}
              </div>
            </div>
          </Accordion>
        </div>
      </>
    );
  }

  // Desktop: Render as horizontal layout
  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="flex items-center gap-3">
      {/* Visitors Search */}
      <div
        ref={visitorRef}
        className="relative flex-1"
        onMouseEnter={() => {
          // Clear any pending close timeout
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
        }}
        onMouseLeave={() => {
          // Delay closing to allow mouse movement to dropdown — but never close while the
          // input is still focused (e.g. user is typing with the mouse elsewhere on screen).
          // Focus/blur/click-outside own the "should this close" decision; hover is only
          // a convenience to preview/open, never to abandon an active search.
          closeTimeoutRef.current = setTimeout(() => {
            if (document.activeElement === visitorInputRef.current) return;
            setActiveDropdown(null);
            setSelectedIndex(-1);
          }, 150);
        }}
      >
        <div
          className="relative"
          onMouseEnter={() => {
            setActiveDropdown('visitor');
            setSelectedIndex(-1);
            visitorInputRef.current?.focus();
          }}
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            ref={visitorInputRef}
            type="text"
            placeholder="Buscar visitante ou prestador de serviço"
            value={visitorSearch}
            onChange={(e) => setVisitorSearch(e.target.value)}
            onFocus={() => {
              setActiveDropdown('visitor');
              setSelectedIndex(-1);
            }}
            onKeyDown={(e) => handleKeyDown(e, 'visitor')}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            data-allow-tab="true"
            className={cn(
              "w-full pl-9 py-2 bg-gray-900 text-gray-200 text-sm rounded-lg border focus:outline-none focus:border-blue-500 placeholder-gray-500",
              visitorSearch ? "pr-8" : "pr-12",
              activeDropdown === null ? "pulse-border" : "border-gray-800"
            )}
          />
          {visitorSearch ? (
            <button
              onClick={() => clearSearch('visitor')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-800 rounded"
            >
              <X className="h-3.5 w-3.5 text-gray-500" />
            </button>
          ) : (
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-gray-800 text-gray-400 rounded border border-gray-700">
              F2
            </kbd>
          )}
        </div>

        {activeDropdown === 'visitor' && (
          <div data-dropdown="true" className="absolute top-full mt-1 left-0 right-0 bg-gray-950 rounded-lg shadow-2xl border border-gray-800 overflow-hidden z-50 max-h-64 overflow-y-auto custom-scrollbar">
            {/* Cadastrar novo visitante button - always shown */}
            {onRegisterVisitor && (
              <button
                onClick={() => {
                  // Parse search term to extract CPF or name
                  const searchTerm = visitorSearch.trim();
                  const prefilledData: { cpf?: string; firstName?: string; lastName?: string } = {};

                  if (isCPFFormat(searchTerm)) {
                    prefilledData.cpf = normalizeCPF(searchTerm);
                  } else if (searchTerm) {
                    // Split by space to get first and last name
                    const nameParts = searchTerm.split(' ').filter(part => part.length > 0);
                    if (nameParts.length > 0) {
                      prefilledData.firstName = nameParts[0];
                      if (nameParts.length > 1) {
                        prefilledData.lastName = nameParts.slice(1).join(' ');
                      }
                    }
                  }

                  onRegisterVisitor(prefilledData);
                  setActiveDropdown(null);
                  setVisitorSearch('');
                }}
                data-allow-tab="true"
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-green-600 hover:bg-green-700 transition-colors text-left border-b border-gray-800"
              >
                <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white/20">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">Cadastrar novo visitante</p>
                  <p className="text-xs text-green-100">
                    {visitorSearch.trim()
                      ? isCPFFormat(visitorSearch)
                        ? `CPF: ${formatCPF(visitorSearch)}`
                        : `Nome: ${visitorSearch}`
                      : 'Clique para adicionar um novo visitante'
                    }
                  </p>
                </div>
              </button>
            )}
            {/* Search results - only shown when there's text */}
            {visitorSearch.trim() && renderResults(visitorResults, visitorSearch, 'visitor')}
          </div>
        )}
      </div>

      {/* People Search (Residents/Employees) */}
      <div
        ref={peopleRef}
        className="relative flex-1"
        onMouseEnter={() => {
          // Clear any pending close timeout
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
        }}
        onMouseLeave={() => {
          // Delay closing to allow mouse movement to dropdown — but never close while the
          // input is still focused (e.g. user is typing with the mouse elsewhere on screen).
          closeTimeoutRef.current = setTimeout(() => {
            if (document.activeElement === peopleInputRef.current) return;
            setActiveDropdown(null);
            setSelectedIndex(-1);
          }, 150);
        }}
      >
        <div
          className="relative"
          onMouseEnter={() => {
            setActiveDropdown('people');
            setSelectedIndex(-1);
            peopleInputRef.current?.focus();
          }}
        >
          <PeopleIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            ref={peopleInputRef}
            type="text"
            placeholder={`Buscar ${peopleLabel.toLowerCase()}`}
            value={peopleSearch}
            onChange={(e) => setPeopleSearch(e.target.value)}
            onFocus={() => {
              setActiveDropdown('people');
              setSelectedIndex(-1);
            }}
            onKeyDown={(e) => handleKeyDown(e, 'people')}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            data-allow-tab="true"
            className={cn(
              "w-full pl-9 py-2 bg-gray-900 text-gray-200 text-sm rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 placeholder-gray-500",
              peopleSearch ? "pr-8" : "pr-12"
            )}
          />
          {peopleSearch ? (
            <button
              onClick={() => clearSearch('people')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-800 rounded"
            >
              <X className="h-3.5 w-3.5 text-gray-500" />
            </button>
          ) : (
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-gray-800 text-gray-400 rounded border border-gray-700">
              F3
            </kbd>
          )}
        </div>

        {activeDropdown === 'people' && (
          <div data-dropdown="true" className="absolute top-full mt-1 left-0 right-0 bg-gray-950 rounded-lg shadow-2xl border border-gray-800 overflow-hidden z-50 max-h-64 overflow-y-auto custom-scrollbar">
            {/* Cadastrar novo button - always shown */}
            {customerType === 'empresa' ? onRegisterEmployee && (
              <button
                onClick={() => {
                  onRegisterEmployee();
                  setActiveDropdown(null);
                  setPeopleSearch('');
                }}
                data-allow-tab="true"
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-green-600 hover:bg-green-700 transition-colors text-left border-b border-gray-800"
              >
                <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white/20">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">Cadastrar novo funcionário</p>
                  <p className="text-xs text-green-100">Clique para adicionar um novo funcionário</p>
                </div>
              </button>
            ) : onRegisterResident && (
              <button
                onClick={() => {
                  onRegisterResident();
                  setActiveDropdown(null);
                  setPeopleSearch('');
                }}
                data-allow-tab="true"
                className="w-full flex items-center gap-3 px-3 py-2.5 bg-green-600 hover:bg-green-700 transition-colors text-left border-b border-gray-800"
              >
                <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 bg-white/20">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">Cadastrar novo morador</p>
                  <p className="text-xs text-green-100">Clique para adicionar um novo morador</p>
                </div>
              </button>
            )}
            {/* Search results - only shown when there's text */}
            {peopleSearch.trim() && renderResults(peopleResults, peopleSearch, 'people')}
          </div>
        )}
      </div>

      {/* Location Search (Units/Departments) */}
      <div
        ref={locationRef}
        className="relative flex-1"
        onMouseEnter={() => {
          // Clear any pending close timeout
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
        }}
        onMouseLeave={() => {
          // Delay closing to allow mouse movement to dropdown — but never close while the
          // input is still focused (e.g. user is typing with the mouse elsewhere on screen).
          closeTimeoutRef.current = setTimeout(() => {
            if (document.activeElement === locationInputRef.current) return;
            setActiveDropdown(null);
            setSelectedIndex(-1);
          }, 150);
        }}
      >
        <div
          className="relative"
          onMouseEnter={() => {
            setActiveDropdown('location');
            setSelectedIndex(-1);
            locationInputRef.current?.focus();
          }}
        >
          <LocationIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            ref={locationInputRef}
            type="text"
            placeholder={`Buscar ${locationLabel.toLowerCase()}`}
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            onFocus={() => {
              setActiveDropdown('location');
              setSelectedIndex(-1);
            }}
            onKeyDown={(e) => handleKeyDown(e, 'location')}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            data-allow-tab="true"
            className={cn(
              "w-full pl-9 py-2 bg-gray-900 text-gray-200 text-sm rounded-lg border border-gray-800 focus:outline-none focus:border-blue-500 placeholder-gray-500",
              locationSearch ? "pr-8" : "pr-12"
            )}
          />
          {locationSearch ? (
            <button
              onClick={() => clearSearch('location')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-800 rounded"
            >
              <X className="h-3.5 w-3.5 text-gray-500" />
            </button>
          ) : (
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-gray-800 text-gray-400 rounded border border-gray-700">
              F4
            </kbd>
          )}
        </div>

        {activeDropdown === 'location' && (
          <div data-dropdown="true" className="absolute top-full mt-1 left-0 right-0 bg-gray-950 rounded-lg shadow-2xl border border-gray-800 overflow-hidden z-50 max-h-64 overflow-y-auto custom-scrollbar">
            {!locationSearch.trim() ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Digite para buscar {customerType === 'empresa' ? 'departamentos' : 'unidades'}
              </div>
            ) : (
              renderResults(locationResults, locationSearch, 'location')
            )}
          </div>
        )}
      </div>
    </div>

    {/* Re-entry Confirmation Modal */}
    <ReentryConfirmationModal
      isOpen={showReentryConfirmation}
      onClose={handleReentryCancel}
      onConfirm={handleReentryConfirm}
      visitorInfo={reentryVisitorInfo ? {
        personName: reentryVisitorInfo.personName,
        unit: reentryVisitorInfo.unit,
        entryTime: reentryVisitorInfo.entryTime
      } : {
        personName: '',
        unit: '',
        entryTime: ''
      }}
    />
    </>
  );
};


// Wrap in React.memo for performance
export const QuickSearchBar = React.memo(QuickSearchBarComponent);
